import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MobileSheet } from '../mobile'
import { mobile } from '../mobile/tokens'

export interface PunchInJobOption {
  id: string
  job_number: string
  title: string
  property?: {
    name?: string | null
    address?: string | null
  } | null
}

interface PunchInJobPickerSheetProps {
  open: boolean
  jobs: PunchInJobOption[]
  busy?: boolean
  onClose: () => void
  onSelectJob: (job: PunchInJobOption) => void | Promise<void>
  onSelectOther: (reason: string) => void | Promise<void>
}

function jobNumberLabel(jobNumber: string): string {
  const raw = jobNumber.trim()
  if (!raw) return ''
  return raw.match(/^JOB-/i) ? raw : `JOB-${raw}`
}

function jobButtonLabel(job: PunchInJobOption): { title: string; subtitle: string } {
  const num = jobNumberLabel(job.job_number)
  const name =
    (job.title || '').trim() ||
    (job.property?.name || '').trim() ||
    (job.property?.address || '').trim() ||
    'Job'
  const place = [job.property?.name, job.property?.address]
    .map((p) => (p || '').trim())
    .find((p) => p && p !== name)
  const subtitle = [num, place].filter(Boolean).join(' · ')
  return { title: name, subtitle }
}

export function PunchInJobPickerSheet({
  open,
  jobs,
  busy,
  onClose,
  onSelectJob,
  onSelectOther,
}: PunchInJobPickerSheetProps) {
  const { t } = useTranslation(['field', 'common'])
  const [otherMode, setOtherMode] = useState(false)
  const [otherText, setOtherText] = useState('')

  useEffect(() => {
    if (!open) {
      setOtherMode(false)
      setOtherText('')
    }
  }, [open])

  const otherTrimmed = otherText.trim()

  return (
    <MobileSheet
      open={open}
      onClose={busy ? () => undefined : onClose}
      title={t('field:picker.title')}
    >
      {otherMode ? (
        <div className="space-y-3">
          <label className={mobile.label} htmlFor="punch-in-other">
            {t('field:picker.otherPlaceholder')}
          </label>
          <textarea
            id="punch-in-other"
            value={otherText}
            onChange={(e) => setOtherText(e.target.value)}
            disabled={busy}
            rows={3}
            className={`${mobile.field} min-h-[96px] resize-y`}
            placeholder={t('field:picker.otherPlaceholder')}
            autoFocus
          />
          <button
            type="button"
            disabled={busy || !otherTrimmed}
            onClick={() => void onSelectOther(otherTrimmed)}
            className={`${mobile.primaryBtn} w-full`}
          >
            {t('field:picker.confirmOther')}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              setOtherMode(false)
              setOtherText('')
            }}
            className={`${mobile.secondaryBtn} w-full`}
          >
            {t('common:back')}
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-gray-500 px-0.5 mb-1">{t('field:picker.subtitle')}</p>
          {jobs.map((job) => {
            const label = jobButtonLabel(job)
            return (
              <button
                key={job.id}
                type="button"
                disabled={busy}
                onClick={() => void onSelectJob(job)}
                className={`${mobile.secondaryBtn} w-full !justify-start !h-auto min-h-[48px] py-3 text-left`}
              >
                <span className="min-w-0">
                  <span className="block truncate">{label.title}</span>
                  {label.subtitle ? (
                    <span className="block text-xs font-normal text-gray-500 truncate mt-0.5">
                      {label.subtitle}
                    </span>
                  ) : null}
                </span>
              </button>
            )
          })}
          <button
            type="button"
            disabled={busy}
            onClick={() => setOtherMode(true)}
            className={`${mobile.secondaryBtn} w-full`}
          >
            {t('field:picker.other')}
          </button>
        </div>
      )}
    </MobileSheet>
  )
}
